/**
 * Pure helpers for the Home "My Open Tasks" list (Bill 2026-10-16). The due wording matches the
 * "Cart processed" Teams card (CartProcessedCardFactory.DueText) so the app and the card say the same thing.
 */
import type { ApplicationTask, ApplicationTaskGroup } from '@retail-ops/shared-types';

export type DueTone = 'overdue' | 'today' | 'soon' | 'later' | 'none' | 'done';

export interface DueInfo {
  tone: DueTone;
  /** Short chip text: "Overdue · Jan 6", "Due today", "Due Thu Jan 8", "No due date set". */
  label: string;
}

const MONTH = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' });

/** yyyy-MM-dd → a UTC-midnight Date (calendar day, no time-zone drift). */
function day(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
}

function daysBetween(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** Due status of one date relative to the server's "today" (both yyyy-MM-dd). */
export function dueInfo(dueDate: string | null | undefined, today: string, done = false): DueInfo {
  if (done) return { tone: 'done', label: 'Done' };
  const d = dueDate ? day(dueDate) : null;
  const t = day(today);
  if (!d || !t) return { tone: 'none', label: 'No due date set' };
  const diff = daysBetween(t, d);
  if (diff < 0) return { tone: 'overdue', label: `Overdue · ${MONTH.format(d)}` };
  if (diff === 0) return { tone: 'today', label: 'Due today' };
  return { tone: diff <= 2 ? 'soon' : 'later', label: `Due ${WEEKDAY.format(d)} ${MONTH.format(d)}` };
}

/** The group's chip: its most urgent open task. */
export function groupDue(g: ApplicationTaskGroup, today: string): DueInfo {
  if (g.openCount === 0) return { tone: 'done', label: 'All done' };
  return dueInfo(g.nextDue, today);
}

/** Every task in the group, accounts first, then application-wide. */
export function allTasks(g: ApplicationTaskGroup): ApplicationTask[] {
  return [...g.accounts.flatMap((a) => a.tasks), ...g.applicationTasks];
}

/**
 * The account number in full. CHG-0147 (Reid's review 10/30; Jessica OK'd 11/2, ROP is in-house): the FSR works
 * the task against the whole number, so the Home task list no longer shows only the last four.
 */
export function acctNumber(acctId?: string | null): string {
  return (acctId ?? '').replace(/\D/g, '');
}

/** "Premier Money Market 20009998138 · Free Checking 20001231234" (at most `max` accounts, then "+n more"). */
export function accountsLine(g: ApplicationTaskGroup, max = 2): string {
  const parts = g.accounts.map((a) => [a.product, acctNumber(a.acctId)].filter(Boolean).join(' '));
  if (parts.length <= max) return parts.join(' · ');
  return `${parts.slice(0, max).join(' · ')} · +${parts.length - max} more`;
}

/** Initials for the customer avatar ("Hank Test" → "HT"). */
export function initials(name: string): string {
  const words = name.replace(/\+\s*\d+$/, '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? words[words.length - 1]?.[0] ?? '' : '';
  return (first + last).toUpperCase();
}

/** The officer who holds most of a group's open tasks (Bill 2026-10-17: the group shows the officer, like the cart panels). */
export function holderOf(g: ApplicationTaskGroup): string | null {
  const counts = new Map<string, number>();
  for (const t of allTasks(g)) {
    if (t.done || !t.assignedOfficerCode) continue;
    const c = t.assignedOfficerCode.toUpperCase();
    counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  let best: string | null = null;
  for (const [c, n] of counts) if (best === null || n > (counts.get(best) ?? 0)) best = c;
  return best ?? (g.assignees?.[0]?.toUpperCase() || null);
}
